import { inject, Injectable, OnDestroy } from '@angular/core';
import { AlertController } from '@ionic/angular';
import { BehaviorSubject, catchError, Observable, of, retry, Subject, takeUntil, timer } from 'rxjs';
import { environment } from 'src/environments/environment';

export interface SSEConnectionStatus {
  status: 'connecting' | 'connected' | 'disconnected' | 'error' | 'reconnecting';
  error?: Error | Event;
  reconnectAttempt?: number;
}

@Injectable({ providedIn: 'root' })
export class UserNotificationsSseService implements OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private readonly maxReconnectAttempts = 5;
  private readonly reconnectDelay = 1000;
  private readonly connectionStatus$ = new BehaviorSubject<SSEConnectionStatus>({ status: 'disconnected' });
  private currentEventSource?: EventSource;
  private currentUrl?: string;
  private readonly alert = inject(AlertController);

  connect(username: string, section?: string): Observable<string> {
    let url = `${environment.endpointBasePaths.infoAPIUrl}info-api/v1/notifications/stream/${encodeURIComponent(
      username
    )}`;
    if (section != null && section !== '') {
      url += `?section=${encodeURIComponent(section)}`;
    }
    this.currentUrl = url;
    return this.createSSEConnection().pipe(
      retry({
        count: this.maxReconnectAttempts,
        delay: (error, retryCount) => {
          console.warn(`User SSE reconectando... Intento ${retryCount}/${this.maxReconnectAttempts}`, error);
          this.connectionStatus$.next({ status: 'reconnecting', reconnectAttempt: retryCount });
          const delay = this.reconnectDelay * Math.pow(2, retryCount - 1);
          return timer(Math.min(delay, 30000));
        },
      }),
      catchError((error) => {
        console.error('Error final en User SSE después de reintentos:', error);
        this.connectionStatus$.next({ status: 'error', error });
        return of('');
      }),
      takeUntil(this.destroy$)
    );
  }

  private createSSEConnection(): Observable<string> {
    return new Observable<string>((observer) => {
      try {
        if (!this.currentUrl) throw new Error('User SSE URL no inicializada');

        this.connectionStatus$.next({ status: 'connecting' });

        if (this.currentEventSource) {
          this.currentEventSource.close();
        }

        this.currentEventSource = new EventSource(this.currentUrl, { withCredentials: false });

        this.currentEventSource.onopen = () => {
          this.connectionStatus$.next({ status: 'connected' });
          console.log('Conexión User SSE establecida');
        };

        // El backend emite eventos con `event: notify|connection|error`, por lo que no llegan por `onmessage`.
        // Usamos addEventListener para capturar el tipo correcto.
        this.currentEventSource.addEventListener('notify', (event: MessageEvent) => {
          if (event.data && event.data.trim() !== '') {
            observer.next(event.data);
          }
        });

        // Útil para diagnóstico: NO_SESSION, etc.
        this.currentEventSource.addEventListener('error', (event: MessageEvent) => {
          console.warn('[UserNotify SSE] event:error recibido:', event.data);
          // No cerramos la conexión: solo informamos.
        });

        // Conectividad del flujo (no se usa para mensajes de negocio).
        this.currentEventSource.addEventListener('connection', (event: MessageEvent) => {
          if (event.data && event.data.trim() !== '') {
            console.log('[UserNotify SSE] event:connection recibido:', event.data);
          }
        });

        // Mantener fallback por si el backend alguna vez emite `event: message`.
        this.currentEventSource.onmessage = (event) => {
          if (event.data && event.data.trim() !== '') {
            observer.next(event.data);
            if (event.data.includes('notify')) {
              // Patrón para extraer el valor del campo 'data' en el string recibido por SSE
              // El patrón busca líneas que comiencen con 'data:' y captura todo lo que sigue, incluso espacios, hasta fin de línea
              const dataMatch = event.data.match(/^data:\s*(.*)$/m); // NOSONAR: HTML from trusted content source (getPage), not user input.
              if (dataMatch?.[1]) {
                const dataValue = dataMatch[1];
                console.log("[UserNotify SSE] Valor del campo 'data' extraído:", dataValue);
                this.showNotificationAlert(dataValue);
              }
            }
          }
        };

        this.currentEventSource.onerror = (error) => {
          console.error('Error en User SSE:', error);
          this.connectionStatus$.next({ status: 'error', error });
          observer.error(new Error('User SSE connection error'));
        };
      } catch (error) {
        this.connectionStatus$.next({
          status: 'error',
          error: error instanceof Error ? error : new Error('Unknown EventSource error'),
        });
        observer.error(error);
      }

      return () => {
        if (this.currentEventSource) {
          this.currentEventSource.close();
          this.currentEventSource = undefined;
          this.connectionStatus$.next({ status: 'disconnected' });
        }
      };
    });
  }

  getConnectionStatus(): Observable<SSEConnectionStatus> {
    return this.connectionStatus$.asObservable();
  }

  /** Cierra la conexión SSE (útil cuando se recibe NO_SESSION y se quiere reconectar). */
  close(): void {
    if (this.currentEventSource) {
      this.currentEventSource.close();
      this.currentEventSource = undefined;
      this.connectionStatus$.next({ status: 'disconnected' });
    }
  }

  ngOnDestroy(): void {
    if (this.currentEventSource) {
      this.currentEventSource.close();
      this.currentEventSource = undefined;
    }
    this.destroy$.next();
    this.destroy$.complete();
    this.connectionStatus$.complete();
  }

  async showNotificationAlert(message: string): Promise<void> {
    const alert = await this.alert.create({
      // header: 'Repetir jugada',
      message: message,
      buttons: [
        // { text: 'Cancelar', role: 'cancel' },
        { text: 'Entendido', role: 'confirm' },
      ],
    });
    await alert.present();
  }
}
